import React, { useState, useEffect } from 'react';
import { Box, VStack, HStack, Switch } from 'native-base';
import AmountDropdown from './dropdowns/Amount';
import PaymentDropdown from './dropdowns/Payment';
import { useGeneralStyles } from '~/shared/styles';
import {
    GiftCard,
    GiftCardPayment,
    observer,
    i18nFn,
    useActivateGiftCard,
    useReloadGiftCard,
    useSpringBigStore,
    useCreateGiftCardWithPoints
} from '@springbig/shared';
import { Caption, CaptionError } from '~/shared/Typography';
import ButtonSpinner from '~/shared/ButtonSpinner';
import Button from '~/shared/Button';
import PointsConverterField from '~/pages/giftCard/pointsConverter/PointsConverterField';

const AddFunds = ({
    manageGiftCard,
    t,
    successToast,
    isPurchaseWithPoints
}: {
    manageGiftCard: GiftCard | null,
    t: i18nFn,
    successToast: (title: string, description?: string) => void,
    isPurchaseWithPoints: boolean;
}) => {

    const { member, merchant } = useSpringBigStore();

    const maxAmount = Math.min(
        Number(member?.remaining_dollar_amount_for_current_period) / 100,
        Number(merchant?.gc_dollar_amount_one_time)
    );

    const [isFundWithPoints, setIsFundWithPoints] = useState(isPurchaseWithPoints);
    const [convertPoints, setConvertPoints] = useState(0);
    const [pointsAmount, setPointsAmount] = useState(0);
    const [amount, setAmount] = useState<number>(merchant.gift_card_restrictions ? maxAmount : 20.00);
    const [belowAmount, setbelowAmount] = useState<number>(
        parseFloat(manageGiftCard?.auto_reload_amount?.split('$')[0] || '20')
    );
    const [paymentMethod, setPaymentMethod] = useState<GiftCardPayment | null>(null);
    const [submitError, setSubmitError] = useState('');

    const {
        mutate: requestActivateGiftCard,
        error: requestActivateCardError,
        isLoading: activateGiftCardLoading
    } = useActivateGiftCard();

    const {
        mutate: reloadGiftCard,
        error: reloadGiftCardError,
        isLoading: reloadGiftCardLoading
    } = useReloadGiftCard();

    const {
        mutate: createWithPoints,
        error: createWithPointsError,
        isLoading: createWithPointsLoading,
    } = useCreateGiftCardWithPoints();

    useEffect(() => {

        requestActivateCardError && setSubmitError(t('general.submitError'));
    }, [requestActivateCardError, t]);

    useEffect(() => {

        reloadGiftCardError && setSubmitError(t('general.submitError'));
    }, [reloadGiftCardError, t]);


    const gStyle = useGeneralStyles();
    const isLoading = activateGiftCardLoading || reloadGiftCardLoading || createWithPointsLoading;
    const isBusy = !member || !merchant || isLoading;
    const isSubmitEnabled =
        (
            ((isFundWithPoints && pointsAmount) || ((paymentMethod && amount) || convertPoints)) &&
            !isBusy &&
            (!merchant?.gift_card_restrictions || amount <= maxAmount)
        );

    const onAddFunds = () => {

        if (!isSubmitEnabled) {
            return;
        }

        if (isFundWithPoints) {
            createWithPoints(
                {
                    member_id: member.id,
                    convert_points: convertPoints,
                    purchase_location: '0'
                },
                {
                    onSuccess: (_) => {

                        observer.emit('close-actionsheet');
                        successToast(t('giftCard.addFunds.createSuccess'));
                    },
                },
            );
        }
        else {
            if (manageGiftCard) {
                reloadGiftCard({
                    member_id: member?.id.toString() || '',
                    card_pin: manageGiftCard.card_pin,
                    card_number: manageGiftCard.card_number,
                    balance: amount.toString(),
                    auto_reload: manageGiftCard.auto_reload,
                    auto_reload_amount: (belowAmount.toString() || '20.00'),
                    purchase_location: '0',
                    gift_card_payment_id: paymentMethod?.id || 0,
                }, {
                    onSuccess: (_) => {

                        observer.emit('close-actionsheet');
                        successToast(t('giftCard.addFunds.reloadSuccess'));
                    }
                });
                return;
            }

            requestActivateGiftCard({
                balance: amount.toString(),
                auto_reload: !!belowAmount,
                auto_reload_amount: belowAmount.toString() || '0.00',
                purchase_location: '0',
                gift_card_payment_id: paymentMethod?.id || 0,
                member_id: member?.id.toString() || ''
            }, {
                onSuccess: (_) => {

                    observer.emit('close-actionsheet');
                    successToast(t('giftCard.addFunds.createSuccess'));
                }
            });
        }
    };

    return (
        <Box width='100%' alignItems='center' >
            <Caption fontSize={24}>{ t('giftCard.addFunds.title') }</Caption>
            <VStack width='100%' space={3} flexGrow={1} mt={8} >
                {
                    merchant.points_cash_conversion_enabled &&
                    merchant.create_gift_card_with_points &&
                    !manageGiftCard &&
                    member.balance >= parseFloat(merchant.dollars_per_point) &&
                    <HStack justifyContent={'space-between'}>
                        <Caption fontSize={18} fontWeight={300}>
                            { t('giftCard.addFunds.fundWithPoints', [merchant.gift_card_alias]) }
                        </Caption>
                        <Switch
                            onTrackColor={gStyle.button_background}
                            isChecked={isFundWithPoints}
                            onToggle={() => setIsFundWithPoints(!isFundWithPoints)}
                        />
                    </HStack>
                }
                { !isFundWithPoints && merchant && !merchant.hide_gift_card_payments &&
                    <VStack>
                        <Caption fontSize={18} fontWeight={300}>
                            { t('general.amount') }
                        </Caption>
                        <AmountDropdown
                            t={t}
                            amount={amount}
                            setAmount={setAmount}
                        />
                    </VStack>
                }
                { !isFundWithPoints && merchant && !merchant.hide_gift_card_payments &&
                    <VStack>
                        <Caption fontSize={18} fontWeight={300}>
                            { t('general.payment') }
                        </Caption>
                        <PaymentDropdown
                            paymentMethod={paymentMethod}
                            setPaymentMethod={setPaymentMethod}
                            t={t}
                        />
                    </VStack>
                }
                { isFundWithPoints &&
                    <PointsConverterField t={t} onChange={(value) => setConvertPoints(value)} />
                }
                {
                    !isFundWithPoints &&
                    member?.customer_gift_card_fee &&
                    member?.customer_gift_card_fee !== '$0.00' &&
                    <Caption textAlign='center'>
                        { t('giftCard.addFunds.conditions.fees', [member?.customer_gift_card_fee]) }
                    </Caption>
                }
                {
                    (
                        requestActivateCardError || reloadGiftCardError
                    ) &&
                    <CaptionError>
                        {
                            (requestActivateCardError as any)?.data?.message ||
                            (reloadGiftCardError as any)?.data?.message ||
                            (createWithPointsError as any)?.data?.message
                        }
                    </CaptionError>
                }
                { submitError && (!requestActivateCardError || !reloadGiftCardError) &&
                    <CaptionError>
                        { submitError }
                    </CaptionError>
                }
                <Button
                    onPress={onAddFunds}
                    backgroundColor={gStyle.button_background}
                    borderRadius={10}
                    isDisabled={!isSubmitEnabled}
                    width='55%'
                    alignSelf={'center'}
                >
                    { isLoading ?
                        <ButtonSpinner />
                        :
                        <Caption
                            color={gStyle.button_text}
                            fontSize={20}
                        >
                            { t('general.add')} ${isFundWithPoints ? convertPoints / parseFloat(merchant?.dollars_per_point || '0.0') : amount }
                        </Caption>
                    }
                </Button>
            </VStack>
        </Box>
    );
};

export default AddFunds;
